import { Typography } from "@hellocarbo/lasagne";
import { useTranslation } from "react-i18next";

import {
  ReviewScope,
  ReviewScopes,
  Scopes as TScopes,
  ScopeTypes,
  TCarbonUnit,
} from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";
import { sortDotDecimalStrings } from "@/utils/consolidation";

import ScopeTableExport from "./ScopeTableExport";

function Scopes({
  scopesData,
  type,
}: {
  scopesData: ReviewScopes;
  type: ScopeTypes;
}) {
  const { t } = useTranslation("scopes");

  // We calculate the unit from the totals since these are the largest values displayed.
  const scopesUnit = getCarbonDisplayUnit(
    Object.values(scopesData).map((scopeData) => scopeData.total)
  );

  return (
    <div className="gap-y-sm flex flex-col">
      <div className="flex justify-between">
        <Typography step="1" weight="bold">
          {t(`scopes:title.${type}`)}
        </Typography>
        <ScopeTableExport
          scopeType={type}
          segmentEventName={
            type === "ghg_protocol"
              ? "ghg_export_downloaded"
              : "beges_export_downloaded"
          }
        />
      </div>
      <div className="gap-y-lg pr-xs flex max-h-[32rem] flex-col overflow-y-auto">
        {Object.keys(scopesData).map((scope) => (
          <Scope
            key={scope}
            scope={scope}
            data={scopesData[scope]}
            type={type}
            unit={scopesUnit}
          />
        ))}
      </div>
    </div>
  );
}

function Scope({
  scope,
  data,
  type,
  unit,
}: {
  scope: string;
  data: ReviewScope;
  type: ScopeTypes;
  unit: TCarbonUnit;
}) {
  const { t } = useTranslation(["scopes", "common", "formatter"]);
  const { total, ...subScopes } = data;

  const sortedSubScopes = sortDotDecimalStrings(Object.keys(subScopes)).reduce(
    (obj, key) => {
      obj[key] = subScopes[key];
      return obj;
    },
    {} as ReviewScope
  );

  const { formattedCarbon: formattedCarbonTotal } = formatCarbon(
    t,
    total,
    unit
  );

  return (
    <table className="border-spacing-y-2xs border-separate">
      <thead>
        <tr>
          <td className="w-full max-w-0">
            <Typography
              step="-2"
              weight="semibold"
              as="h5"
              className="truncate"
            >
              {t("scopes:scope-title", {
                number: scope,
                name: t(`scopes:${type}.${scope}`),
              })}
            </Typography>
          </td>
          <Typography
            step="-2"
            weight="semibold"
            as="td"
            className="text-right whitespace-nowrap"
          >
            {formattedCarbonTotal}
            &nbsp;
            {t(`common:unit.${unit}`)}
          </Typography>
        </tr>
      </thead>
      <tbody>
        {Object.keys(sortedSubScopes).map((subScope) => {
          const { formattedCarbon: formattedSubScope } = formatCarbon(
            t,
            subScopes[subScope],
            unit
          );

          return (
            <tr key={subScope}>
              <Typography
                step="-2"
                weight="semibold"
                as="td"
                className="border-grayscale-x-light pt-2xs text-grayscale max-w-0 truncate border-t"
              >
                {subScope}
                &nbsp;
                {t(`scopes:${type}.${subScope.toString().replace(".", "-")}`)}
              </Typography>
              <Typography
                step="-2"
                as="td"
                className="border-grayscale-x-light pt-2xs text-grayscale-dark border-t text-right whitespace-nowrap"
              >
                {formattedSubScope}
                &nbsp;
                {t(`common:unit.${unit}`)}
              </Typography>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ReviewByScopes({ scopes }: { scopes: TScopes }) {
  return (
    <div className="gap-x-lg grid h-full grid-cols-2">
      <Scopes scopesData={scopes.beges} type={ScopeTypes.beges} />
      <Scopes scopesData={scopes.ghg_protocol} type={ScopeTypes.ghg} />
    </div>
  );
}

export default ReviewByScopes;
